import React, { useCallback, useEffect, useRef } from 'react';
import { Animated, Easing, View } from 'react-native';

import { Pressable, Text, createMqStyles, tokens } from '@fhs/ui';

import { StoreLocatorServiceModeTab } from '../../types';

interface ServiceModeToggleProps {
  value: StoreLocatorServiceModeTab;
  onChange: (value: StoreLocatorServiceModeTab) => void;
}

interface ToggleOptionProps {
  label: string;
  isActive: boolean;
  onPress: () => void;
  testID: string;
}

// A fixed 260 track centred in the panel, per the design, rather than a full-bleed control.
// These stay in sync with the `container`/`thumb` styles below and the sliding-thumb geometry.
// Per Figma (node 173:6024 mobile / 164:6586 desktop), the track's padding is asymmetric:
// 7px horizontal, 5px vertical (giving 40px/42px tall pills inside the 50px/52px track) —
// not a uniform 7px inset.
const TOGGLE_WIDTH = 260;
const TOGGLE_PADDING_HORIZONTAL = 7;
const TOGGLE_PADDING_VERTICAL = 5;
const OPTION_GAP = 6;
const THUMB_WIDTH = (TOGGLE_WIDTH - TOGGLE_PADDING_HORIZONTAL * 2 - OPTION_GAP) / 2;
const THUMB_TRANSLATE_X = THUMB_WIDTH + OPTION_GAP;

const TOGGLE_ANIMATION_DURATION = 220;

const ToggleOption = ({ label, isActive, onPress, testID }: ToggleOptionProps) => {
  const styles = useServiceModeToggleMqStyles();

  return (
    <Pressable
      testID={testID}
      role="radio"
      aria-checked={isActive}
      accessibilityLabel={label}
      dd-action-name={`store locator ${label}`}
      onPress={onPress}
      borderRadius={999}
      borderWidth={0}
      style={styles.option}
      hoveredStyle={isActive ? undefined : styles.optionHovered}
      outlineStyle={styles.hideOutline}
      outlinePressedStyle={styles.hideOutline}
      outlineFocusedStyle={styles.hideOutline}
      outlineHoveredStyle={styles.hideOutline}
    >
      <Text.Ui
        size="md"
        weight="semibold"
        style={[styles.optionLabel, isActive && styles.optionLabelActive]}
      >
        {label}
      </Text.Ui>
    </Pressable>
  );
};

/**
 * Pickup / Delivery segmented control. There is no segmented-control primitive in `@fhs/ui`, so
 * this is a purpose-built pair of pills matching the locator design.
 *
 * Radio semantics rather than tabs: the locator's Nearby / Previous / Favorites bar is the page's
 * tablist, and a second one here would make the two indistinguishable to assistive tech.
 */
export const ServiceModeToggle: React.VFC<ServiceModeToggleProps> = ({ value, onChange }) => {
  const styles = useServiceModeToggleMqStyles();
  const isDelivery = value === StoreLocatorServiceModeTab.DELIVERY;

  // Drives the sliding highlight below. Seeding it from the initial value avoids a
  // spurious slide-in animation when the screen lands directly on the delivery tab.
  const progress = useRef(new Animated.Value(isDelivery ? 1 : 0)).current;

  useEffect(() => {
    Animated.timing(progress, {
      toValue: isDelivery ? 1 : 0,
      duration: TOGGLE_ANIMATION_DURATION,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start();
  }, [isDelivery, progress]);

  const selectPickup = useCallback(() => onChange(StoreLocatorServiceModeTab.PICKUP), [onChange]);
  const selectDelivery = useCallback(
    () => onChange(StoreLocatorServiceModeTab.DELIVERY),
    [onChange]
  );

  return (
    <View
      style={styles.container}
      role="radiogroup"
      aria-label="Order type"
      accessibilityLabel="Order type"
    >
      <Animated.View
        pointerEvents="none"
        style={[
          styles.thumb,
          {
            transform: [
              {
                translateX: progress.interpolate({
                  inputRange: [0, 1],
                  outputRange: [0, THUMB_TRANSLATE_X],
                }),
              },
            ],
          },
        ]}
      />
      <ToggleOption
        testID="store-locator-pickup-toggle"
        label="Pickup"
        isActive={!isDelivery}
        onPress={selectPickup}
      />
      <ToggleOption
        testID="store-locator-delivery-toggle"
        label="Delivery"
        isActive={isDelivery}
        onPress={selectDelivery}
      />
    </View>
  );
};

const useServiceModeToggleMqStyles = createMqStyles({
  container: {
    $base: {
      alignSelf: 'center',
      backgroundColor: tokens.colors.$white,
      borderColor: tokens.colors.$black10,
      borderRadius: 999,
      borderWidth: 1,
      flexDirection: 'row',
      gap: OPTION_GAP,
      height: 50,
      maxWidth: '100%',
      paddingHorizontal: TOGGLE_PADDING_HORIZONTAL,
      paddingVertical: TOGGLE_PADDING_VERTICAL,
      width: TOGGLE_WIDTH,
    },
    $gteDesktop: {
      height: 52,
    },
  },
  // Absolutely positioned so it slides beneath the (transparent-background) options rather
  // than participating in the row's flex layout.
  thumb: {
    $base: {
      backgroundColor: tokens.colors.$houseRed,
      borderRadius: 999,
      bottom: TOGGLE_PADDING_VERTICAL,
      left: TOGGLE_PADDING_HORIZONTAL,
      position: 'absolute',
      top: TOGGLE_PADDING_VERTICAL,
      width: THUMB_WIDTH,
    },
  },
  option: {
    $base: {
      alignItems: 'center',
      backgroundColor: tokens.colors.$transparent,
      borderRadius: 999,
      flex: 1,
      justifyContent: 'center',
      paddingHorizontal: 10,
    },
  },
  optionHovered: {
    $base: {
      backgroundColor: tokens.colors.$blackOpacity04,
    },
  },
  optionLabel: {
    $base: {
      color: tokens.colors.$houseDark,
    },
  },
  optionLabelActive: {
    $base: {
      color: tokens.colors.$white,
    },
  },
  hideOutline: {
    $base: {
      borderColor: tokens.colors.$transparent,
    },
  },
});
